import type { Analytics } from 'firebase/analytics'
import { app } from './firebase'
import { BUILD } from './build'

// firebase/analytics is imported dynamically so it lands in its own chunk rather than the
// entry bundle. Nothing renders behind it, and a blocked or slow load is already handled.
type AnalyticsModule = typeof import('firebase/analytics')
let mod: AnalyticsModule | null = null

/**
 * Google Analytics 4 through Firebase (measurement id G-YMBHPNZK0L).
 *
 * Two rules govern what is sent:
 *
 * 1. **No personal data in event parameters.** GA4's terms prohibit it, and this is a
 *    client-facing tool — an email address in an event name would put Kevin's and Sarah's
 *    identities into Google's reporting. Role and organisation are sent instead, which
 *    answer the questions worth asking ("is the client using the decisions tab?") without
 *    identifying anyone. `setUserId` takes the Firebase uid, which is the one identifier
 *    GA is designed to receive.
 *
 * 2. **Never let analytics break the app.** Every call is wrapped. Ad blockers make
 *    failures routine rather than exceptional, and a blocked beacon must not take a tab
 *    switch down with it.
 */

let analytics: Analytics | null = null
let ready = false

export async function initAnalytics(): Promise<void> {
  if (ready) return
  ready = true
  try {
    mod = await import('firebase/analytics')
    // False in unsupported environments, and when measurementId is absent.
    if (!(await mod.isSupported())) return
    analytics = mod.getAnalytics(app)
    mod.setUserProperties(analytics, { app_version: BUILD.version, build_sha: BUILD.sha })
  } catch {
    analytics = null
  }
}

export function identify(uid: string, role: string, org: 'milestone' | 'client') {
  try {
    if (!analytics || !mod) return
    mod.setUserId(analytics, uid)
    mod.setUserProperties(analytics, { role, org })
  } catch { /* analytics must never throw into the app */ }
}

export function clearIdentity() {
  try {
    if (!analytics || !mod) return
    mod.setUserId(analytics, null)
  } catch { /* ignore */ }
}

type Params = Record<string, string | number | boolean>

export function track(event: string, params: Params = {}) {
  try {
    if (!analytics || !mod) return
    mod.logEvent(analytics, event, params)
  } catch { /* ignore */ }
}

/** GA4's own page_view, fired per tab since this is a single-page app. */
export function trackTab(tab: string) {
  track('page_view', {
    page_title: tab,
    page_location: `${window.location.origin}/#${tab}`,
    page_path: `/${tab}`,
  })
}
